import React, { useState } from 'react';
import { supervisedStudentApi } from '@/features/workspace/api';
import { useAuth } from '@/hooks/useAuth';
import '../b2-literature.css';

interface LiteratureSearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onLiteratureAdded?: () => void;
}

interface SearchItem {
  id: string;
  title: string;
  authors: string;
  year: number;
  source: string;
  doi: string;
  database: string;
  openAccess: boolean;
  note: string;
  url: string;
  shared?: boolean;
}

const DATABASES = ['Scopus', 'Web of Science', 'Crossref / DOI', 'Open repositories', 'Институциональная библиотека'];

function asItem(raw: Record<string, unknown>): SearchItem {
  const doi = String(raw.doi || raw.doi_url || '');
  const url = String(raw.url || raw.file_url || (doi ? `https://doi.org/${doi.replace(/^https?:\/\/doi.org\//, '')}` : ''));
  const section = String(raw.category_section || '');
  return {
    id: String(raw.id || raw.title || ''),
    title: String(raw.title || ''),
    authors: String(raw.authors || raw.author || ''),
    year: Number(raw.year || 0) || new Date().getFullYear(),
    source: String(raw.journal || raw.source || raw.category_section || ''),
    doi,
    database: String(raw.database || raw.index_db || ''),
    openAccess: raw.open_access !== false && raw.is_open_access !== false,
    note: String(raw.snippet || raw.abstract || raw.note || ''),
    url,
    shared: section.includes('Совмест') || String(raw.note || '').includes('SHARED'),
  };
}

export const LiteratureSearchOverlay: React.FC<LiteratureSearchOverlayProps> = ({
  isOpen,
  onClose,
  onLiteratureAdded,
}) => {
  const { user } = useAuth();
  const courseStudent = Boolean(user?.roles?.includes('COURSE_STUDENT') && !user?.roles?.includes('STUDENT') && !user?.roles?.includes('SYSTEM_OWNER'));
  const [view, setView] = useState<'search' | 'results' | 'saved'>('search');
  const [topic, setTopic] = useState('academic writing language education');
  const [discipline, setDiscipline] = useState('Лингвистика');
  const [sourceType, setSourceType] = useState('Статьи + обзоры');
  const [period, setPeriod] = useState('Последние 5 лет');
  const [languages, setLanguages] = useState('English + Русский + Қазақша');
  const [databases, setDatabases] = useState<string[]>(['Scopus', 'Web of Science', 'Crossref / DOI', 'Open repositories']);
  const [openAccessOnly, setOpenAccessOnly] = useState(true);
  const [limit, setLimit] = useState('10');
  const [extra, setExtra] = useState('');
  const [results, setResults] = useState<SearchItem[]>([]);
  const [saved, setSaved] = useState<SearchItem[]>([]);
  const [preview, setPreview] = useState<SearchItem | null>(null);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const toggleDatabase = (name: string) => {
    setDatabases((current) => (current.includes(name) ? current.filter((item) => item !== name) : [...current, name]));
  };

  const search = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setLoading(true);
    setNotice('');
    setPreview(null);
    try {
      const response = await supervisedStudentApi.literatureSearch({
        query: topic,
        discipline,
        source_type: sourceType,
        period,
        languages,
        databases,
        open_access_only: openAccessOnly,
        limit: Number(limit) || 10,
        additional_restrictions: extra || null,
      });
      const items = (response?.items || []).map((item: Record<string, unknown>) => asItem(item));
      setResults(items);
      setView('results');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Не удалось выполнить поиск.');
    } finally {
      setLoading(false);
    }
  };

  const loadSaved = async () => {
    setView('saved');
    setPreview(null);
    if (typeof supervisedStudentApi.listLiterature !== 'function') return;
    try {
      const rows = await supervisedStudentApi.listLiterature();
      setSaved((rows || []).map((item: Record<string, unknown>) => asItem(item)));
    } catch {
      setNotice('Не удалось открыть сохранённую литературу.');
    }
  };

  const persist = async (item: SearchItem, shared: boolean) => {
    try {
      await supervisedStudentApi.addLiterature({
        title: item.title,
        author: item.authors,
        doi_url: item.doi || null,
        file_url: item.url || null,
        category_section: shared ? 'Совместная литература' : 'Личная библиотека',
        note: shared ? `STUDENT recommendation · DOI ${item.doi}` : item.note,
      });
      onLiteratureAdded?.();
      setSaved((current) => {
        const next = current.filter((row) => row.title !== item.title);
        return [{ ...item, shared }, ...next];
      });
      setView('saved');
      setNotice('');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Не удалось сохранить источник.');
    }
  };

  const back = () => {
    if (view !== 'search') {
      setView('search');
      setPreview(null);
      return;
    }
    onClose();
  };

  return (
    <div className="b2-root" role="dialog" aria-modal="true">
      <div className="b2-shell">
        <header className="b2-top">
          <button type="button" onClick={back}>← Назад</button>
          <div>
            <h1>Найти литературу</h1>
            <p>{courseStudent ? 'COURSE_STUDENT' : 'STUDENT'} · поиск, open access и сохранение источников</p>
          </div>
          <button type="button" onClick={onClose}>Закрыть ×</button>
        </header>
        <main className="b2-content">
          {view === 'search' ? (
            <>
              <section className="b2-hero">
                <small>Поиск литературы · {courseStudent ? 'COURSE STUDENT' : 'STUDENT'}</small>
                <h2>Поиск научной литературы</h2>
                <p>Короткая форма из ключевых параметров. Scopus, Web of Science и библиотечные подписки подключаются только через официальные подключения.</p>
              </section>
              <section className="b2-panel">
                <header>
                  <div>
                    <h3>Параметры поиска</h3>
                    <p>9 компактных вопросов — без длинной анкеты.</p>
                  </div>
                  <span className="b2-chip">5–10 QUESTIONS</span>
                </header>
                <div className="b2-body">
                  <form onSubmit={(event) => void search(event)}>
                    <div className="b2-form">
                      <div className="b2-field full">
                        <label>1. Тема / ключевые понятия *</label>
                        <textarea required value={topic} onChange={(event) => setTopic(event.target.value)} />
                      </div>
                      <div className="b2-field">
                        <label>2. Дисциплина</label>
                        <select value={discipline} onChange={(event) => setDiscipline(event.target.value)}>
                          <option>Лингвистика</option>
                          <option>Образование</option>
                          <option>Филология</option>
                          <option>Междисциплинарное исследование</option>
                        </select>
                      </div>
                      <div className="b2-field">
                        <label>3. Тип источников</label>
                        <select value={sourceType} onChange={(event) => setSourceType(event.target.value)}>
                          <option>Статьи + обзоры</option>
                          <option>Только статьи</option>
                          <option>Книги / главы</option>
                          <option>Все академические материалы</option>
                        </select>
                      </div>
                      <div className="b2-field">
                        <label>4. Период</label>
                        <select value={period} onChange={(event) => setPeriod(event.target.value)}>
                          <option>Последние 5 лет</option>
                          <option>Последние 10 лет</option>
                          <option>Без ограничения</option>
                        </select>
                      </div>
                      <div className="b2-field">
                        <label>5. Языки</label>
                        <select value={languages} onChange={(event) => setLanguages(event.target.value)}>
                          <option>English + Русский + Қазақша</option>
                          <option>English</option>
                          <option>Қазақша</option>
                          <option>Русский</option>
                        </select>
                      </div>
                      <div className="b2-field full">
                        <label>6. Базы / источники</label>
                        <div className="b2-checks">
                          {DATABASES.map((name) => (
                            <label key={name}>
                              <input type="checkbox" checked={databases.includes(name)} onChange={() => toggleDatabase(name)} />
                              {name}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="b2-field">
                        <label>7. Полный текст</label>
                        <label className="b2-checks">
                          <span>
                            <input type="checkbox" checked={openAccessOnly} onChange={(event) => setOpenAccessOnly(event.target.checked)} />
                            Только open access / легально доступный PDF
                          </span>
                        </label>
                      </div>
                      <div className="b2-field">
                        <label>8. Количество результатов</label>
                        <select value={limit} onChange={(event) => setLimit(event.target.value)}>
                          <option>10</option>
                          <option>20</option>
                          <option>30</option>
                          <option>50</option>
                        </select>
                      </div>
                      <div className="b2-field full">
                        <label>9. Дополнительные ограничения</label>
                        <input value={extra} placeholder="Например: исключить материалы ниже 2020 года" onChange={(event) => setExtra(event.target.value)} />
                      </div>
                    </div>
                    <div className="b2-actions">
                      <button type="button" className="b2-btn" onClick={() => void loadSaved()}>Моя сохранённая литература</button>
                      <span className="spacer" />
                      <button className="b2-btn gold" type="submit" disabled={loading}>{loading ? 'Поиск…' : 'Найти литературу →'}</button>
                    </div>
                  </form>
                </div>
              </section>
              <div className="b2-note"><b>Доступ к источникам:</b> закрытые Scopus/WoS данные не скрапятся и не сохраняют пользовательские credentials. Подключение выполняется через лицензированный/институциональный доступ; open full text — только по законному URL.</div>
            </>
          ) : null}
          {view === 'results' ? (
            <>
              <section className="b2-hero">
                <small>Параметры поиска</small>
                <h2>Результаты поиска</h2>
                <p>Библиография, DOI, база, open-access статус, официальный или full-text link и сохранение.</p>
              </section>
              {preview ? <PreviewCard item={preview} onClose={() => setPreview(null)} /> : null}
              <section className="b2-panel">
                <header>
                  <div>
                    <h3>Найденные материалы</h3>
                    <p>Каждая запись проходит metadata verification.</p>
                  </div>
                  <span className="b2-chip">{results.length} Результаты</span>
                </header>
                <div className="b2-body">
                  <div className="b2-results">
                    {results.length ? results.map((item) => (
                      <ResultCard
                        key={item.id}
                        item={item}
                        courseStudent={courseStudent}
                        onPreview={() => setPreview(item)}
                        onSave={() => void persist(item, false)}
                        onShare={() => void persist(item, true)}
                      />
                    )) : <div className="b2-empty">По этим параметрам источники не найдены.</div>}
                  </div>
                </div>
              </section>
              <div className="b2-actions">
                <button type="button" className="b2-btn" onClick={() => setView('search')}>← Изменить параметры</button>
                <span className="spacer" />
                <button type="button" className="b2-btn" onClick={() => void loadSaved()}>Сохранённые →</button>
              </div>
            </>
          ) : null}
          {view === 'saved' ? (
            <>
              <section className="b2-hero">
                <small>Личная исследовательская библиотека</small>
                <h2>Моя сохранённая литература</h2>
                <p>{courseStudent
                  ? 'Сохранённые источники образуют личную библиотеку обучающегося и доступны для повторного использования в учебных и исследовательских задачах.'
                  : 'Сохранённые источники принадлежат текущему обучающемуся. Отдельная кнопка отправляет запись в shared literature рабочего supervision-контекста.'}</p>
              </section>
              <section className="b2-panel">
                <header>
                  <div>
                    <h3>Сохранённые источники</h3>
                    <p>{saved.length} записей</p>
                  </div>
                </header>
                <div className="b2-body">
                  <div className="b2-results">
                    {saved.length ? saved.map((item) => (
                      <article key={item.id} className="b2-result" style={{ ['--a' as string]: '#49ddb7' }}>
                        <div>
                          <h4>{item.title}</h4>
                          <p>{item.authors} · {item.year} · DOI {item.doi || '—'}</p>
                          <div className="b2-meta">
                            <span className="b2-chip oa">СОХРАНЕНО</span>
                            {item.shared ? <span className="b2-chip">Совместная литература</span> : null}
                          </div>
                        </div>
                        <aside>
                          {courseStudent ? <span className="b2-chip oa">ЛИЧНАЯ БИБЛИОТЕКА</span> : (
                            <button type="button" className="b2-btn" onClick={() => void persist(item, true)}>
                              {item.shared ? 'Повторно открыть shared' : 'В совместную литературу'}
                            </button>
                          )}
                        </aside>
                      </article>
                    )) : <div className="b2-empty">Пока ничего не сохранено.</div>}
                  </div>
                </div>
              </section>
              <div className="b2-actions">
                <button type="button" className="b2-btn" onClick={() => setView('search')}>← Новый поиск</button>
              </div>
            </>
          ) : null}
          {notice ? <p className="b2-note">{notice}</p> : null}
        </main>
      </div>
    </div>
  );
};

const ResultCard: React.FC<{
  item: SearchItem;
  courseStudent: boolean;
  onPreview: () => void;
  onSave: () => void;
  onShare: () => void;
}> = ({ item, courseStudent, onPreview, onSave, onShare }) => (
  <article className="b2-result" style={{ ['--a' as string]: item.openAccess ? '#49ddb7' : '#e2ad3a' }}>
    <div>
      <h4>{item.title}</h4>
      <p>{item.authors} · {item.year}<br />{item.source}</p>
      <div className="b2-meta">
        {item.doi ? <span className="b2-chip">DOI {item.doi}</span> : null}
        <span className={`b2-chip${item.openAccess ? ' oa' : ''}`}>{item.openAccess ? 'OPEN ACCESS · FULL TEXT' : 'METADATA / ACCESS CHECK'}</span>
        {item.database ? <span className="b2-chip">{item.database}</span> : null}
      </div>
      {item.note ? <p style={{ marginTop: 8 }}>{item.note}</p> : null}
    </div>
    <aside>
      <button type="button" className="b2-btn" onClick={onPreview}>Открыть карточку</button>
      <button type="button" className="b2-btn mint" onClick={onSave}>Сохранить себе</button>
      {courseStudent ? null : <button type="button" className="b2-btn" onClick={onShare}>В совместную литературу</button>}
    </aside>
  </article>
);

const PreviewCard: React.FC<{ item: SearchItem; onClose: () => void }> = ({ item, onClose }) => (
  <section className="b2-panel">
    <header>
      <div>
        <h3>{item.title}</h3>
        <p>{item.authors} · {item.year}</p>
      </div>
      <button type="button" className="b2-btn" onClick={onClose}>Закрыть карточку</button>
    </header>
    <div className="b2-body">
      <p>DOI: {item.doi || '—'}</p>
      <p>{item.source}</p>
      {item.note ? <p>{item.note}</p> : null}
      {item.url ? <p><a href={item.url} target="_blank" rel="noreferrer">{item.url}</a></p> : null}
    </div>
  </section>
);
